import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReviewKind } from '../../shared/progress';
import { markOnboardingSeen } from '../lib/onboardingSeen';
import type { SceneId } from '../lib/tourScenes';
import Button from './Button';
import TourDemoScene from './TourDemoScene';

interface TourStep {
  title: string;
  /** One entry per paragraph. */
  body: string[];
  /** Small faint footnote under the body/demo, e.g. the one-editor-at-a-time
   *  caveat on the suggest-edits step. */
  disclaimer?: string;
  scene: SceneId | null;
}

/**
 * The five steps, with the document's noun swapped on an interview brief. The
 * animated scenes swap with it: each scene has a script variant and a brief
 * variant (titled serif sections instead of an AUDIO / VISUAL table), picked
 * by `kind` in src/client/lib/tourScenes.ts and the stage components.
 */
function stepsFor(kind: ReviewKind): TourStep[] {
  const brief = kind === 'brief';
  return [
    {
      title: brief ? 'Welcome to your interview brief.' : 'Welcome to your script.',
      body: [
        `This is a quick tour that shows you how to use this tool to review your ${
          brief ? 'brief' : 'script'
        } and give feedback.`,
        brief
          ? "Once you've used this once it's the same for every brief and script you receive in future :)"
          : "Once you've used this once it's the same for every script you receive in future :)",
      ],
      scene: null,
    },
    {
      title: 'How to use the comments feature',
      body: [
        // A brief has no visuals to change, so that example goes.
        brief
          ? "Highlight anything in the brief (a line you love, a question, an idea you have) and leave a note right there. It's the fastest way to give us specific feedback."
          : "Highlight anything in the script (a line you love, a question, a visual to change, an idea you have) and leave a note right there. It's the fastest way to give us specific feedback.",
      ],
      scene: 'comments',
    },
    {
      title: 'How to use the suggest edits feature',
      body: [
        `Prefer to just make the change? Suggest edits turns the ${
          brief ? 'brief' : 'script'
        } into a live document: type directly, and it autosaves as you go. We'll see exactly what you changed.`,
      ],
      disclaimer:
        'If reviewing with a team, please have one person suggest edits at a time to ensure changes are reflected correctly.',
      scene: 'suggest-edits',
    },
    {
      title: 'How to use the general notes feature',
      body: [
        "Not much feedback, or only small changes? Anything that doesn't belong to one line (tone, overall direction, priorities) goes here, signed with your name.",
      ],
      scene: 'general-notes',
    },
    {
      title: 'How to send your feedback',
      body: [
        "Once you've left your feedback via your preferred method: comment, suggest an edit, or leave a general note.",
        "Send it back to us and we'll take it from there.",
      ],
      scene: null,
    },
  ];
}

const HEADING_ID = 'onboarding-tour-heading';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

function getFocusable(container: HTMLElement | null): HTMLElement[] {
  if (!container) return [];
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) => el.offsetParent !== null,
  );
}

interface Props {
  /** Re-entry mode ("See the tour again"): Escape, backdrop click, and a
   *  visible ✕ all close it — it's already been seen, there's nothing left
   *  to gate. Omitting this (the first-visit default) is the hard gate:
   *  none of those three work, Next/Start reviewing/Skip are the only ways
   *  out. */
  dismissible?: boolean;
  /** Admin's "Allow skipping the tour" toggle. Only rendered as a quiet
   *  footer link while NOT dismissible — a re-entered tour already has
   *  Escape/backdrop/✕, so a second exit hatch would be clutter. */
  skippable?: boolean;
  /** Swaps the document's noun through the copy and picks the brief or script
   *  variant of each animated scene. */
  kind?: ReviewKind;
  onClose: () => void;
}

// Exit fade duration (ms). Opacity only, no scale/slide — matches the
// suggest-mode transition overlay's "fade only, no wipe" brand rule.
const EXIT_FADE_MS = 200;

/**
 * The first-visit onboarding tour, per mockups/onboarding-tour.html. Follows
 * the house modal recipe (fixed inset-0 bg-ink-900/70 backdrop-blur-md …
 * z-50, animate-fade-in/animate-scale-in) but, being hard-gated by default,
 * has to be better-behaved than the modals that recipe otherwise covers
 * (ApprovalCeremony, ContactModal): a real focus trap, focus restored to the
 * invoking element on close, and focus moved to the new heading on every
 * step change so screen readers pick up the change.
 *
 * "Seen" is written on completion (Start reviewing) or Skip, never on mere
 * display — abandoning the tab mid-tour re-shows it next visit. A dismissed
 * re-entry doesn't need to write it again; it's already there.
 *
 * Closing (any path) fades the backdrop and modal out over EXIT_FADE_MS
 * before actually unmounting, so focus restore and the fade never race —
 * see closeWithFade below.
 */
export default function ClientOnboardingTour({
  dismissible = false,
  skippable = false,
  kind = 'script',
  onClose,
}: Props) {
  const steps = useMemo(() => stepsFor(kind), [kind]);
  const lastStep = steps.length - 1;
  const [step, setStep] = useState(0);
  const [manualPlay, setManualPlay] = useState(false);
  const [closing, setClosing] = useState(false);
  const closingRef = useRef(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const closeTimeoutRef = useRef<number | null>(null);
  const reduceMotion = useRef(
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  ).current;

  const isLastStep = step === lastStep;
  const showSkipLink = skippable && !dismissible;

  // Every close path (Start reviewing, Skip, and Escape/backdrop/✕ where
  // dismissible) routes through here so the fade is consistent everywhere.
  // Under reduced motion, close instantly — no fade, no delay — same as
  // before this change. Otherwise fade for EXIT_FADE_MS, then unmount:
  // `onClose` is what actually removes this component from ClientReview's
  // tree, so the mount effect's cleanup (which restores focus) fires right
  // after the fade completes, not before it starts.
  function closeWithFade() {
    // Guarded by a ref, not the `closing` state: the document-level keydown
    // listener is wired once on mount and holds this function's first-render
    // closure, where `closing` is false forever. With the state guard alone,
    // hammering Escape during the fade would schedule a fresh onClose timer
    // per press (each overwriting closeTimeoutRef, so cleanup only clears the
    // last) and fire onClose repeatedly. The ref reads current truth from any
    // stale closure.
    if (closingRef.current) return;
    closingRef.current = true;
    if (reduceMotion) {
      onClose();
      return;
    }
    setClosing(true);
    closeTimeoutRef.current = window.setTimeout(onClose, EXIT_FADE_MS);
  }

  function finish() {
    markOnboardingSeen();
    closeWithFade();
  }

  function handleSkip() {
    markOnboardingSeen();
    closeWithFade();
  }

  function handleDismiss() {
    if (!dismissible) return;
    closeWithFade();
  }

  function goNext() {
    if (isLastStep) {
      finish();
    } else {
      // Reset synchronously in the same handler that changes the step, not
      // via a reactive effect keyed on step — an effect fires after the
      // step's first render, so the new step would transiently mount with
      // the previous step's manualPlay still true, i.e. an autoplaying
      // video flashing in (and downloading) against a reduced-motion
      // client's preference for one frame. Batched with setStep, so the
      // new step's very first render already sees manualPlay reset.
      setManualPlay(false);
      setStep((s) => Math.min(lastStep, s + 1));
    }
  }

  function goBack() {
    setManualPlay(false);
    setStep((s) => Math.max(0, s - 1));
  }

  // Mount: focus the step heading (NOT the first focusable control — that's
  // the Skip link, and parking focus there painted a focus ring on it the
  // moment the tour opened, reading as if Skip were highlighted/recommended).
  // The heading is tabIndex={-1} with outline-none, so nothing visibly rings,
  // screen readers announce the dialog title, and the first Tab press still
  // lands on Skip via the trap below. Also: trap Tab/Shift+Tab inside the
  // modal, wire Escape (only acts when dismissible), and restore focus to
  // whatever invoked the tour on unmount.
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    (headingRef.current ?? modalRef.current)?.focus();

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        handleDismiss();
        return;
      }
      if (e.key !== 'Tab') return;
      const items = getFocusable(modalRef.current);
      if (items.length === 0) {
        e.preventDefault();
        modalRef.current?.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      // Focus isn't always sitting on one of `items`: the step heading is
      // focused programmatically after every step change (tabIndex={-1}, so
      // it's deliberately excluded from `items` — it's not meant to be a Tab
      // stop) but IS the active element in that window, and native Tab
      // traversal doesn't know about our trap and would walk straight past
      // it to whatever's next in the whole document — including "See the
      // tour again" behind the modal, which would hand out Escape/backdrop
      // exits and defeat the hard gate. Treat "active is inside the modal
      // but isn't a real item" exactly like "active is outside the modal
      // entirely": either way, pull it back onto first (Tab) or last
      // (Shift+Tab) rather than only special-casing the two wrap points.
      const activeIsItem = active !== null && items.includes(active as HTMLElement);
      if (e.shiftKey) {
        if (!activeIsItem || active === first) {
          e.preventDefault();
          last.focus();
        }
      } else if (!activeIsItem || active === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      previouslyFocused?.focus?.();
      // Defensive only: onClose already fired (via closeWithFade's timeout)
      // by the time this component actually unmounts, so the timer's own
      // callback never reaches here in the normal flow. Guards against a
      // stray timer if something else unmounts us mid-fade.
      if (closeTimeoutRef.current !== null) window.clearTimeout(closeTimeoutRef.current);
    };
    // onClose/handleDismiss intentionally omitted (redefined every render,
    // and functionally gated on `dismissible` alone) — re-running this
    // effect on every render would thrash the mount-focus + previouslyFocused
    // capture logic above. `dismissible` is included since it's the one
    // value from this closure that actually changes the Escape behavior.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dismissible]);

  // Move focus to the heading on every step change so assistive tech
  // announces it and keyboard Tab order restarts predictably from the top of
  // the new content. This also fires on the initial mount, where it focuses
  // the same heading the mount effect just focused — a harmless no-op, so no
  // first-run guard is needed (an earlier version guarded this because mount
  // used to focus the first CONTROL instead; see the mount effect).
  useEffect(() => {
    headingRef.current?.focus();
  }, [step]);

  const current = steps[step];
  const isMediaStep = current.scene !== null;

  return (
    <div
      className={`fixed inset-0 bg-ink-900/70 backdrop-blur-md flex items-end md:items-center justify-center z-50 ${
        closing ? 'animate-fade-out' : 'animate-fade-in'
      }`}
      onClick={dismissible ? handleDismiss : undefined}
    >
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={HEADING_ID}
        onClick={(e) => e.stopPropagation()}
        className={`
          relative flex flex-col w-full md:w-[94vw] ${isMediaStep ? 'md:max-w-[640px]' : 'md:max-w-[480px]'}
          max-h-[92vh] md:max-h-[85vh]
          bg-card border border-line
          rounded-t-2xl md:rounded-md
          transition-[max-width] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]
          shadow-flare-lg/30 ${closing ? 'animate-fade-out' : 'animate-scale-in'}
        `}
      >
        {dismissible && (
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Close"
            className="
              absolute top-4 right-4 z-10
              w-7 h-7 rounded-full bg-elevated text-faint
              flex items-center justify-center
              hover:text-fg transition-colors
            "
          >
            <span aria-hidden>✕</span>
          </button>
        )}

        {/* Grabber — mobile sheet affordance only. */}
        <div className="md:hidden shrink-0 flex justify-center pt-3" aria-hidden>
          <span className="w-9 h-1 rounded-full bg-line" />
        </div>

        <div className="shrink-0 px-6 md:px-10 pt-4 md:pt-7">
          <div className="flex items-center justify-between">
            <span className="font-mono text-[11px] uppercase tracking-[0.12em] font-semibold text-faint">
              Step {step + 1} of {steps.length}
            </span>
            <div className="flex items-center gap-1.5" aria-hidden>
              {steps.map((_, i) => (
                <span
                  key={i}
                  className={`h-1.5 rounded-full transition-all duration-200 ${
                    i === step ? 'w-4 bg-accent' : i < step ? 'w-1.5 bg-accent/40' : 'w-1.5 bg-line'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 md:px-10 pb-2">
          {/* Copy fits naturally at 480/640 (per-step modal width) — no
              separate reading-measure wrapper needed here any more. */}
          <h2
            id={HEADING_ID}
            ref={headingRef}
            tabIndex={-1}
            className="font-display font-bold text-[22px] md:text-[24px] leading-[1.15] tracking-[-0.01em] mt-3 mb-2 outline-none"
          >
            {current.title}
          </h2>
          <div className="space-y-2">
            {current.body.map((paragraph, i) => (
              <p key={i} className="text-muted text-[14px] leading-[1.55]">
                {paragraph}
              </p>
            ))}
          </div>

          {current.scene && (
            <div className="relative aspect-[16/10] rounded-md border border-line bg-elevated overflow-hidden my-4">
              <TourDemoScene
                key={`${kind}:${current.scene}`}
                sceneId={current.scene}
                kind={kind}
                reduceMotion={reduceMotion}
                manualPlay={manualPlay}
                onPlay={() => setManualPlay(true)}
              />
            </div>
          )}

          {/* text-muted, not the step-counter's text-faint: this is a caveat
              the client is actually meant to read, and faint at 11px sits
              below AA contrast on the card. */}
          {current.disclaimer && (
            <p className="text-[11px] text-muted leading-[1.5] mt-3">{current.disclaimer}</p>
          )}

          {isLastStep && (
            /* Center-weighted vertical funnel: one method per line, each
               arrow pointing down into the next, ending on the send step. */
            <div className="flex flex-col items-center gap-1 mt-4 pt-4 border-t border-line">
              <RecapChip n={1} label="Comment" />
              <RecapArrow />
              <RecapChip n={2} label="Suggest edits" />
              <RecapArrow />
              <RecapChip n={3} label="General notes" />
              <RecapArrow />
              <RecapChip n={4} label="Send for review" final />
            </div>
          )}
        </div>

        <div className="shrink-0 flex items-center gap-3 px-6 md:px-10 py-5 md:py-6 border-t border-line">
          {showSkipLink && (
            <button
              type="button"
              onClick={handleSkip}
              className="font-mono text-[11px] uppercase tracking-[0.1em] font-semibold text-faint hover:text-muted transition-colors rounded-sm focus:outline-none focus-visible:shadow-focus"
            >
              Skip tour
            </button>
          )}
          {step > 0 && (
            <Button variant="ghost" size="sm" onClick={goBack}>
              Back
            </Button>
          )}
          <span className="flex-1" />
          <Button variant="primary" glow onClick={goNext}>
            {isLastStep ? 'Start reviewing' : 'Next'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function RecapArrow() {
  return (
    <span className="text-faint text-[12px] leading-none" aria-hidden>
      ↓
    </span>
  );
}

function RecapChip({ n, label, final = false }: { n: number; label: string; final?: boolean }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.06em] font-bold ${
        final ? 'text-accent' : 'text-muted'
      }`}
    >
      <span
        className={`inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] ${
          final ? 'bg-accent text-accent-fg' : 'bg-elevated text-muted'
        }`}
      >
        {n}
      </span>
      {label}
    </span>
  );
}
